import { Final } from "@components/QuestionFlowDialog/components/Final";
import { ERoutes } from "@utils/constants";

import { AccountType } from "@/contexts/AccountTypeContext";

import { ChoiceSection, FindOut } from "../../components";
import { Steps } from "../constants";
import { THandleNextStep } from "../types";

const CheckIcon = () => (
  <svg viewBox="0 0 24 24">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// Single YouTube Video License — one video.
const VideoIcon = () => (
  <svg viewBox="0 0 24 24">
    <rect x="2" y="5" width="20" height="14" rx="3" />
    <polygon points="10 9 16 12 10 15 10 9" />
  </svg>
);

// Subscription — premium / recurring (the diamond used elsewhere for Premium).
const DiamondIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M6 3h12l4 6-10 13L2 9Z" />
  </svg>
);

const LicenseIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
    <path d="M16 13H8" />
    <path d="M16 17H8" />
  </svg>
);

const UserPlusIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <line x1="19" y1="8" x2="19" y2="14" />
    <line x1="22" y1="11" x2="16" y2="11" />
  </svg>
);

const XCircleIcon = () => (
  <svg viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10" />
    <line x1="15" y1="9" x2="9" y2="15" />
    <line x1="9" y1="9" x2="15" y2="15" />
  </svg>
);

// Free needs credit (informational dead-end).
const InfoIcon = () => (
  <svg viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

/* Blue icons for the gold result buttons (match the site's gold-button-with-
   blue-icon style). 17px, stroke/fill #60a3d7. */
const BtnMusic = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#60a3d7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M9 18V5l12-2v13" />
    <circle cx="6" cy="18" r="3" />
    <circle cx="18" cy="16" r="3" />
  </svg>
);
const BtnDiamond = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="#60a3d7" stroke="#60a3d7" strokeWidth="1.5" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M6 3h12l4 6-10 13L2 9Z" />
  </svg>
);
const BtnMail = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#60a3d7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 7 9 6 9-6" />
  </svg>
);
const BtnMic = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#60a3d7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="23" />
    <line x1="8" y1="23" x2="16" y2="23" />
  </svg>
);

export const renderStepContent = (
  activeStep: Steps,
  handleNextStep: THandleNextStep,
  onClose: () => void,
  accountType: AccountType,
) => {
  const isSub = accountType === AccountType.SUBSCRIPTION;
  const restart = () => handleNextStep(Steps.START); // back to the "Start Test" screen

  switch (activeStep) {
    case Steps.START:
      return <FindOut handleLeftChoice={() => handleNextStep()} />;

    // ── Part 1 — which lane? (everyone) ──
    case Steps.MUSIC_USE:
      return (
        <ChoiceSection
          handleLeftChoice={() => handleNextStep()} // On YouTube → continue
          handleRightChoice={() => handleNextStep(Steps.FINAL_CUSTOM_LICENSE)} // Outside YouTube → custom license
          type="musicUse"
        />
      );
    case Steps.ADVERTISEMENT:
      return (
        <ChoiceSection
          handleLeftChoice={() => handleNextStep(Steps.FINAL_CUSTOM_LICENSE)} // Ad → custom license
          handleRightChoice={() => handleNextStep()}
          type="advertisement"
        />
      );
    case Steps.BROADCAST_NETWORK:
      return (
        <ChoiceSection
          handleLeftChoice={() => handleNextStep(Steps.FINAL_CUSTOM_LICENSE)} // Broadcast → custom license
          handleRightChoice={() => handleNextStep()}
          type="broadcastNetwork"
        />
      );
    case Steps.MUSIC_CONTENT:
      return (
        <ChoiceSection
          handleLeftChoice={() => handleNextStep(Steps.FINAL_BECOME_CREATOR)} // Music-focused → creator program
          handleRightChoice={() => handleNextStep()}
          type="musicContent"
        />
      );
    case Steps.LIVESTREAMS:
      return (
        <ChoiceSection
          handleLeftChoice={() => handleNextStep(Steps.FINAL_LIVESTREAMS)} // Livestream → not possible
          // Passed Part 1: subscriber → covered; free/guest → Part 2 (willing-to-pay).
          handleRightChoice={() => handleNextStep(isSub ? Steps.FINAL_FREE_USE : undefined)}
          type="livestreams"
        />
      );

    // ── Part 2 — which product (Free users + guests only) ──
    case Steps.WILLING_TO_PAY:
      return (
        <ChoiceSection
          handleLeftChoice={() => handleNextStep(Steps.FREQUENCY)} // Yes (open to paying) → frequency
          handleRightChoice={() => handleNextStep(Steps.GIVE_CREDIT)} // No (free only) → credit question
          type="willingToPay"
        />
      );
    case Steps.GIVE_CREDIT:
      return (
        <ChoiceSection
          handleLeftChoice={() => handleNextStep(Steps.FINAL_FREE_USE)} // Will credit → free use
          // Prefers free but won't credit: free needs credit, and they don't
          // want to pay — so it's a dead-end message (no subscription/license push).
          handleRightChoice={() => handleNextStep(Steps.FINAL_FREE_NEEDS_CREDIT)}
          type="giveCredit"
        />
      );
    case Steps.FREQUENCY:
      return (
        <ChoiceSection
          handleLeftChoice={() => handleNextStep()} // Single video project → track count
          handleRightChoice={() => handleNextStep(Steps.FINAL_SUBSCRIPTION)} // Ongoing/regular → subscription
          type="frequency"
        />
      );
    case Steps.TRACK_COUNT:
      return (
        <ChoiceSection
          handleLeftChoice={() => handleNextStep(Steps.FINAL_SINGLE_LICENSE)} // One/few tracks → single license
          handleRightChoice={() => handleNextStep(Steps.FINAL_SUBSCRIPTION)} // Several/many → subscription
          type="trackCount"
        />
      );

    // ── Finals (gold button gets a matching blue icon) ──
    case Steps.FINAL_FREE_USE:
      // Free/guest: "Eligible for Free Use". Subscriber: "Covered by Your Subscription" (Final picks the variant copy).
      return <Final type="giveCredit" onClose={onClose} route={ERoutes.Catalog} variant="positive" icon={<CheckIcon />} buttonIcon={<BtnMusic />} onRestart={restart} />;
    case Steps.FINAL_FREE_NEEDS_CREDIT:
      return <Final type="freeNeedsCredit" onClose={onClose} route={ERoutes.Catalog} variant="negative" icon={<InfoIcon />} buttonIcon={<BtnMusic />} onRestart={restart} />;
    case Steps.FINAL_SINGLE_LICENSE:
      return <Final type="singleLicense" onClose={onClose} route={ERoutes.Catalog} variant="positive" icon={<VideoIcon />} buttonIcon={<BtnMusic />} onRestart={restart} />;
    case Steps.FINAL_SUBSCRIPTION:
      return <Final type="subscription" onClose={onClose} route={ERoutes.Pricing} variant="positive" icon={<DiamondIcon />} buttonIcon={<BtnDiamond />} onRestart={restart} />;
    case Steps.FINAL_CUSTOM_LICENSE:
      return <Final type="needLicence" onClose={onClose} route={ERoutes.Licensing} variant="negative" icon={<LicenseIcon />} buttonIcon={<BtnMail />} onRestart={restart} />;
    case Steps.FINAL_BECOME_CREATOR:
      return (
        <Final
          type="becomeCreator"
          onClose={onClose}
          route={ERoutes.MUSIC_CREATOR_PROGRAM}
          variant="negative"
          icon={<UserPlusIcon />}
          buttonIcon={<BtnMic />}
          onRestart={restart}
        />
      );
    case Steps.FINAL_LIVESTREAMS:
      return <Final type="livestreams" onClose={onClose} route={ERoutes.Catalog} variant="error" icon={<XCircleIcon />} buttonIcon={<BtnMusic />} onRestart={restart} />;

    default:
      return null;
  }
};
